// Tracking parameters captured from URL
export interface TrackingParams {
  gclid?: string
  fbclid?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  referrer?: string
  landing_page?: string
}

const STORAGE_KEY = 'aquatic_tracking'

// Capture tracking params from URL on page load
export function captureTrackingParams(): TrackingParams {
  const params = new URLSearchParams(window.location.search)
  
  const tracking: TrackingParams = {
    gclid: params.get('gclid') || undefined,
    fbclid: params.get('fbclid') || undefined,
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
    utm_content: params.get('utm_content') || undefined,
    utm_term: params.get('utm_term') || undefined,
    referrer: document.referrer || undefined,
    landing_page: window.location.href,
  }

  // Store in sessionStorage (persists through form navigation)
  const existing = getStoredTracking()
  const merged = { ...existing, ...tracking }
  
  // Only update if we have new values
  if (Object.values(tracking).some(v => v)) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged))
  }

  return merged
}

// Get stored tracking params
export function getStoredTracking(): TrackingParams {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch {
    return {}
  }
}

// Clear tracking (after successful submission)
export function clearTracking(): void {
  sessionStorage.removeItem(STORAGE_KEY)
}

// Google Tag Manager / gtag.js event tracking
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
    dataLayer?: unknown[]
  }
}

export function trackEvent(
  eventName: string,
  params?: Record<string, unknown>
): void {
  // Google Analytics 4 / gtag
  if (window.gtag) {
    window.gtag('event', eventName, {
      form_id: 'aquatic_lead_form',
      ...params,
    })
  }

  // Google Tag Manager dataLayer
  if (window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      form_id: 'aquatic_lead_form',
      ...params,
    })
  }

  // Console log for debugging (remove in production)
  if (import.meta.env.DEV) {
    console.log('📊 Track:', eventName, params)
  }
}

// Track form step completion
export function trackStepComplete(
  stepNumber: number,
  stepName: string,
  additionalData?: Record<string, unknown>
): void {
  trackEvent('form_step_complete', {
    step_number: stepNumber,
    step_name: stepName,
    ...additionalData,
  })
}

// Track form start
export function trackFormStart(): void {
  trackEvent('form_start', {
    timestamp: new Date().toISOString(),
  })
}

// Track form completion (conversion)
export function trackFormComplete(leadId: string): void {
  // Primary conversion event
  trackEvent('form_complete', {
    lead_id: leadId,
    timestamp: new Date().toISOString(),
  })

  // Google Ads conversion (if configured)
  if (window.gtag && import.meta.env.VITE_AW_CONVERSION_ID) {
    window.gtag('event', 'conversion', {
      send_to: import.meta.env.VITE_AW_CONVERSION_ID,
      value: 1.0,
      currency: 'USD',
    })
  }
}

// Track form abandonment
export function trackFormAbandon(lastStep: number): void {
  trackEvent('form_abandon', {
    last_step: lastStep,
    timestamp: new Date().toISOString(),
  })
}
